import { useState } from "react";
import { ActionMenuItem } from "../../../../components/actionMenu/ActionMenu";
import {
  checkEndpointStatus,
  endpointStatus,
} from "../../../../helpers/apiHelper";
import {
  getRecallEntry,
  postSendRecallEmail,
} from "../../../../redux/api/recall.api";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import {
  RecallClientActionsDataInterface,
  RecallEmailInterface,
} from "../../../../redux/interfaces/recall.interface";
import { setGlobalToast } from "../../../../redux/reducer/globalComponents.reducer";

const emailStatusLabels: Record<RecallEmailInterface["status"], string> = {
  sent: "Email deja trimis",
  pending: "Email in curs",
  unknown: "Email neconfirmat",
};

function useRecallClientEmailAction({
  client,
}: {
  client: RecallClientActionsDataInterface;
}): ActionMenuItem {
  const dispatch = useAppDispatch();
  const entry = useAppSelector((state) => state.recall.entry);
  const isBatchLoading = useAppSelector(
    (state) => state.recall.clientBatch.isLoading,
  );
  const hasPendingClientActions = useAppSelector(
    (state) => state.recall.pendingClientActionIds.length > 0,
  );
  const [isSending, setIsSending] = useState(false);
  const hasEmail = Boolean(client.pharmacyEmail?.trim());
  const disabledReason = !client.document
    ? "Genereaza documentul de retragere inainte de trimiterea emailului."
    : !hasEmail
    ? "Nu are email asignat"
    : client.email
    ? emailStatusLabels[client.email.status]
    : isSending
    ? "Trimiterea emailului este in curs."
    : entry.isLoading
    ? "Se incarca datele retragerii."
    : entry.isError
    ? "Reincarca datele retragerii inainte de trimiterea emailului."
    : isBatchLoading || hasPendingClientActions
    ? "Asteapta finalizarea actiunii in curs pentru farmacii."
    : "";
  const isBusy =
    entry.isLoading ||
    entry.isError ||
    isBatchLoading ||
    hasPendingClientActions;

  const handleSend = async () => {
    if (!client.document || !hasEmail || client.email || isSending || isBusy)
      return;
    setIsSending(true);
    const resultAction = await dispatch(
      postSendRecallEmail({
        idCampaign: client.idCampaign,
        clientPlId: client.clientPlId,
      }),
    );
    const status = checkEndpointStatus(resultAction, postSendRecallEmail);

    if (status === endpointStatus.fulfilled) {
      const email = resultAction.payload as RecallEmailInterface;
      dispatch(
        setGlobalToast({
          open: true,
          vertical: "bottom",
          horizontal: "right",
          severity: email.status === "sent" ? "success" : "warning",
          message:
            email.status === "sent"
              ? `Email acceptat pentru trimitere catre ${email.recipient}.`
              : email.status === "pending"
              ? "Trimiterea emailului este in curs. Verifica rezultatul inainte de alte actiuni."
              : "Trimiterea emailului nu este confirmata. Verifica rezultatul inainte de alte actiuni.",
        }),
      );
    } else if (status === endpointStatus.rejected) {
      // Emailul poate fi deja inregistrat chiar daca raspunsul nu a ajuns.
      await dispatch(getRecallEntry({ id: client.idCampaign }));
    }

    if (status !== endpointStatus.pending) setIsSending(false);
  };

  return {
    id: "send-email",
    label: "Trimite email retragere",
    icon: "SEND_EMAIL",
    tooltip:
      disabledReason ||
      "Trimite documentul de retragere generat la adresa de email asignata farmaciei.",
    onClick: handleSend,
    disabled: Boolean(disabledReason),
    loading: isSending,
  };
}

export default useRecallClientEmailAction;
